/**
 * useProcessPanelProps Hook
 *
 * Transforms props from DataTablePanel into the format expected by the RecordProcessPanel React component.
 * This hook replaces the prop-building logic from visualizer/js/modules/core/processPanel/processPanel.controller.js
 */

import { useMemo, useCallback, useEffect, useState } from "react";
import tableCapability from "@visualizer/modules/core/tableCapability/tableCapability.service";
import VisualizerAbilities from "@viz-ui/services/abilities/visualizerAbilitiesService";

/**
 * Hook to build RecordProcessPanel props from DataTablePanel data
 *
 * @param {Object} params - Input parameters
 * @param {Object} params.selectedRecordData - Data for the selected record
 * @param {number|string} params.selectedRecordId - ID of the selected record
 * @param {Array} params.checkedRecordIds - Array of checked record IDs
 * @param {number} params.checkedRecordCount - Count of checked records
 * @param {Array} params.fieldModels - Field model objects
 * @param {string} params.fieldFormatIdentifier - Field format identifier
 * @param {boolean} params.rowCheckedInverse - Whether selection is inversed
 * @param {boolean} params.isFirstRecord - Whether this is the first record
 * @param {boolean} params.isLastRecord - Whether this is the last record
 * @param {boolean} params.isOpen - Whether the panel is open
 * @param {Object} params.appConfig - Application configuration
 * @param {Object} params.dataModel - Data model service
 * @param {Object} params.processPanelService - Process panel service instance
 * @param {Function} params.onProcessRecord - Callback for processing a record
 * @param {Function} params.onDeleteRecord - Callback for deleting a record
 * @param {Function} params.onAddComment - Callback for adding a comment
 * @param {Function} params.onCheck - Callback for checking a record
 * @param {Function} params.onClosePanel - Callback for closing the panel
 * @param {Function} params.onDeleteBulk - Callback for bulk deletion
 * @param {Function} params.onDeleteInProgress - Callback for delete in progress
 * @param {Function} params.onEnterBulkReviewMode - Callback for entering bulk review mode
 * @param {Function} params.onExitBulkReviewMode - Callback for exiting bulk review mode
 * @param {Function} params.onNextRowClick - Callback for next row navigation
 * @param {Function} params.onPrevRowClick - Callback for previous row navigation
 * @param {Function} params.onProcessBulk - Callback for bulk processing
 * @returns {Object|null} - Props ready for RecordProcessPanel component, or null if not ready
 */
export function useProcessPanelProps({
  selectedRecordData,
  selectedRecordId,
  checkedRecordIds,
  checkedRecordCount,
  fieldModels,
  fieldFormatIdentifier,
  rowCheckedInverse,
  isFirstRecord,
  isLastRecord,
  isOpen,
  appConfig,
  dataModel,
  processPanelService,
  onProcessRecord,
  onDeleteRecord,
  onAddComment,
  onCheck,
  onClosePanel,
  onDeleteBulk,
  onDeleteInProgress,
  onEnterBulkReviewMode,
  onExitBulkReviewMode,
  onNextRowClick,
  onPrevRowClick,
  onProcessBulk,
}) {
  // State for abilities (loaded asynchronously)
  const [abilities, setAbilities] = useState(null);

  // Fetch abilities on mount
  useEffect(() => {
    VisualizerAbilities.fetchAbilities().then(() => {
      setAbilities(VisualizerAbilities.getAbilities());
    });
  }, []);

  // Format row data using ProcessPanelService
  // Extracted from processPanel.controller.js lines 58-71
  const { rowData, characterFieldsRowData } = useMemo(() => {
    if (!selectedRecordData || !fieldModels || !dataModel || !processPanelService) {
      return { rowData: [], characterFieldsRowData: [] };
    }

    const tableConfigColumns = dataModel.tableConfig.columns();

    // Format all visible fields
    const formattedRowData = processPanelService.formatRowData(
      fieldFormatIdentifier,
      selectedRecordData,
      fieldModels,
      tableConfigColumns
    );

    // Format character fields for questionnaires (all visible)
    const formattedCharacterFieldsRowData = processPanelService.formatRowData(
      fieldFormatIdentifier,
      selectedRecordData,
      fieldModels.filter(x => x.type() === "character"),
      tableConfigColumns.map(x => ({ ...x, visible: true }))
    );

    return {
      rowData: formattedRowData,
      characterFieldsRowData: formattedCharacterFieldsRowData,
    };
  }, [selectedRecordData, fieldModels, fieldFormatIdentifier, dataModel, processPanelService]);

  // Wrap onProcessRecord callback
  // Extracted from processPanel.controller.js lines 102-116
  const handleProcessedRecord = useCallback(
    (processed, unprocessed, newFieldData, removeFromView, questionnaireFieldData) => {
      if (!onProcessRecord || !processPanelService) return;

      const metadataFields = processPanelService.fixMetadataNames(newFieldData);
      const oldFields = selectedRecordData;
      const newFields = { ...questionnaireFieldData, ...metadataFields };
      const changedFields = processPanelService.onlyChangedFields(newFields, oldFields);

      onProcessRecord({
        processed,
        unprocessed,
        newFieldData: changedFields,
        removeFromView,
      });
    },
    [selectedRecordData, processPanelService, onProcessRecord]
  );

  // Wrap onDeleteRecord callback
  // Extracted from processPanel.controller.js lines 118-120
  const handleDeletedRecord = useCallback(() => {
    if (onDeleteRecord) {
      onDeleteRecord({ rowId: selectedRecordId });
    }
  }, [selectedRecordId, onDeleteRecord]);

  // Wrap onDeleteInProgress callback
  const handleDeleteInProgress = useCallback(() => {
    if (onDeleteInProgress) {
      onDeleteInProgress();
    }
  }, [onDeleteInProgress]);

  // Wrap onAddComment callback
  // Extracted from processPanel.controller.js lines 124-130
  const handleAddedComment = useCallback(
    (comment, attachment) => {
      if (onAddComment) {
        onAddComment({
          rowId: selectedRecordId,
          hasComment: !!comment,
          hasAttachment: !!attachment,
        });
      }
    },
    [selectedRecordId, onAddComment]
  );

  // Wrap onCheck callback
  // Extracted from processPanel.controller.js lines 82-84
  // RecordProcessPanel calls onCheck(activeRecordId) - we need to forward that ID
  const handleCheck = useCallback(
    rowId => {
      if (onCheck) {
        // Pass the rowId directly (as a number), not wrapped in an object
        // Angular's checkRowCheckbox expects just the rowId number
        onCheck(rowId);
      }
    },
    [onCheck]
  );

  // Wrap onEnterBulkReviewMode callback
  // Extracted from processPanel.controller.js lines 133-135
  const handleClickReviewRecords = useCallback(() => {
    if (onEnterBulkReviewMode) {
      onEnterBulkReviewMode();
    }
  }, [onEnterBulkReviewMode]);

  // Wrap onExitBulkReviewMode callback
  // Extracted from processPanel.controller.js lines 136-138
  const handleCancelReviewRecords = useCallback(() => {
    if (onExitBulkReviewMode) {
      onExitBulkReviewMode();
    }
  }, [onExitBulkReviewMode]);

  // Wrap onProcessBulk callback
  // Extracted from processPanel.controller.js lines 140-148
  const handleProcessedRecords = useCallback(
    (requireDelayJob, processed, unprocessed, removeFromView, fromQuestionnaire) => {
      if (onProcessBulk) {
        onProcessBulk({
          requireDelayJob,
          processed,
          unprocessed,
          fromQuestionnaire,
          removeFromView,
        });
      }
    },
    [onProcessBulk]
  );

  // Wrap onDeleteBulk callback
  // Extracted from processPanel.controller.js lines 150-152
  const handleDeletedRecords = useCallback(
    (deleted, notDeleted) => {
      if (onDeleteBulk) {
        onDeleteBulk({ deleted, notDeleted });
      }
    },
    [onDeleteBulk]
  );

  const selectedRecordsAsNumbers = useMemo(
    () => (checkedRecordIds ? checkedRecordIds.map(id => Number(id)) : []),
    [checkedRecordIds]
  );

  const numSelectedRecords = useMemo(
    () => (rowCheckedInverse ? checkedRecordCount || 0 : checkedRecordIds ? checkedRecordIds.length : 0),
    [checkedRecordIds, rowCheckedInverse, checkedRecordCount]
  );

  const isActiveRecordSelected = useMemo(() => {
    if (!checkedRecordIds || !selectedRecordId) return false;
    const found = checkedRecordIds.indexOf(String(selectedRecordId)) !== -1 ||
                  checkedRecordIds.indexOf(Number(selectedRecordId)) !== -1;
    return rowCheckedInverse ? !found : found;
  }, [checkedRecordIds, selectedRecordId, rowCheckedInverse]);

  // Build final props object
  // Extracted from processPanel.controller.js lines 77-161
  const processPanelProps = useMemo(() => {
    if (!abilities || !dataModel || !appConfig) {
      return null;
    }

    if (!selectedRecordData || selectedRecordId === undefined || selectedRecordId === null) {
      return null;
    }

    const isArchived = dataModel.project.archived();
    const canRemediate = tableCapability.canRemediateRecords();

    const headlineLabelLocalizeKey = canRemediate
      ? "_ProcessPanel.Headline.Label_"
      : "_ProcessPanel.Headline.ViewRecord.Label_";

    const activeRowId = selectedRecordData["metadata.position"];

    return {
      // Title
      headlineLabelLocalizeKey,

      // Record identification
      activeRecordId: selectedRecordId ? Number(selectedRecordId) : selectedRecordId,
      activeRowId: activeRowId,

      // Selection state
      selectedRecords: selectedRecordsAsNumbers,
      isSelectionInversed: rowCheckedInverse,
      numSelectedRecords,
      isActiveRecordSelected,
      onCheck: handleCheck,

      // Panel state
      isOpen,

      // App config values - convert to numbers as RecordProcessPanel expects numbers
      projectId: parseInt(appConfig.project_id, 10) || 0,
      controlId: parseInt(appConfig.control_id, 10) || 0,
      controlTestId: parseInt(appConfig.control_test_id, 10) || 0,

      // Navigation
      isFirstRecord,
      isLastRecord,
      onClickPrevArrow: onPrevRowClick,
      onClickNextArrow: onNextRowClick,

      // Data
      data: rowData,
      characterFieldsData: characterFieldsRowData,
      filters: dataModel.getFilterConfig(),

      // Record processing callbacks
      onProcessedRecord: handleProcessedRecord,
      onDeletedRecord: handleDeletedRecord,
      onDeleteInProgress: handleDeleteInProgress,
      onAddedComment: handleAddedComment,

      // Bulk mode callbacks
      onClickReviewRecords: handleClickReviewRecords,
      onCancelReviewRecords: handleCancelReviewRecords,
      onProcessedRecords: handleProcessedRecords,
      onDeletedRecords: handleDeletedRecords,

      // Permission-based visibility (from abilities)
      // Extracted from processPanel.controller.js lines 154-160
      isGroupSelectorEnabled: !!abilities.can_manage_exceptions_groups,
      isFooterVisible: !!(canRemediate && !isArchived && abilities.can_process_exceptions),
      isDeleteVisible: !!abilities.can_delete_exception,
      isAddCommentVisible: !!(!isArchived && abilities.can_process_exceptions),
      isRecordCheckboxVisible: !!canRemediate,
      isActivityTabVisible: !!canRemediate,

      // Close panel handler
      onClosePanel: onClosePanel,
    };
  }, [
    abilities,
    dataModel,
    appConfig,
    selectedRecordId,
    selectedRecordData,
    selectedRecordsAsNumbers,
    rowCheckedInverse,
    numSelectedRecords,
    isActiveRecordSelected,
    isOpen,
    isFirstRecord,
    isLastRecord,
    rowData,
    characterFieldsRowData,
    handleCheck,
    handleProcessedRecord,
    handleDeletedRecord,
    handleDeleteInProgress,
    handleAddedComment,
    handleClickReviewRecords,
    handleCancelReviewRecords,
    handleProcessedRecords,
    handleDeletedRecords,
    onPrevRowClick,
    onNextRowClick,
    onClosePanel,
  ]);

  return processPanelProps;
}

export default useProcessPanelProps;
